import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal
} from "@angular/core";
import {MessageDetails, MessageLevel} from "../../../notification/message/message";
import {NotificationService} from "../../../notification/notification.service";
import {OrderItem} from '../../order';
import {QuantityControlComponent} from '../../../inputs/quantity-control/quantity-control.component';
import {MenuModel} from '../../../menu/menu.model';

@Component({
    selector: "woa-order-item-quantity-control",
    templateUrl: "./order-item-quantity-control.component.html",
    imports: [
        QuantityControlComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemQuantityControlComponent {
    private notificationService: NotificationService = inject(NotificationService);
    private menuModel: MenuModel = inject(MenuModel);

    public readonly orderItem: InputSignal<OrderItem> = input.required<OrderItem>();
    public readonly originalOrderItem: InputSignal<OrderItem> = input.required<OrderItem>();

    public readonly allowZeroQuantity: InputSignal<boolean> = input<boolean>(false);
    public readonly transparent: InputSignal<boolean> = input<boolean>(false);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);

    public quantityChanged: OutputEmitterRef<void> = output<void>();

    public readonly canDecrement: Signal<boolean> = computed<boolean>(() =>
        !this.disabled() && this.orderItem().quantitySignal() > (this.allowZeroQuantity() ? 0 : (this.orderItem().sellable().minWebOrderQty || 1)));

    public readonly canIncrement: Signal<boolean> = computed<boolean>(() =>
        !this.disabled() && !this.orderItem().isAtLimit()(1, this.orderItem().quantitySignal(), this.originalOrderItem()?.sellableQtyOrdered()));

    async decrementQuantity(): Promise<void> {
        if (!this.canDecrement()) {
            if (this.orderItem().sellable().minWebOrderQty > 1) {
                return this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.message.order.productMinimumReached", {
                    item: this.menuModel.getMenuItemDisplayName(this.orderItem().menuItem()),
                    minimum: this.orderItem().sellable().minWebOrderQty
                }));
            }
            return;
        }
        this.orderItem().decrementQuantity(this.orderItem().quantity <= this.orderItem().sellable().minWebOrderQty ? this.orderItem().quantity : 1);
        this.quantityChanged.emit();
    }

    async incrementQuantity(): Promise<void> {
        if (!this.canIncrement()) {
            return this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.message.order.productLimitReached", {
                item: this.menuModel.getMenuItemDisplayName(this.orderItem().menuItem())
            }));
        }
        this.orderItem().incrementQuantity(this.orderItem().quantity < this.orderItem().sellable().minWebOrderQty ? (this.orderItem().sellable().minWebOrderQty - this.orderItem().quantity) : 1);
        this.quantityChanged.emit();
    }
}
